/**
 * editRequest — "drill into this item" as a signal a widget can subscribe to.
 *
 * Drill-in (caret for text, crop for an image, file picker for empty media)
 * is implemented INSIDE each widget, behind a per-page `PageEditContext`. The
 * selection action bar lives outside every page, so it cannot reach that
 * context — and threading it out would couple the bar to page internals. A
 * module-level request bus (same shape as `altHeld`) keeps both sides simple:
 * the bar asks by item id, the widget that owns that id answers with the same
 * code path its second click runs.
 *
 * Each request carries a sequence number so two requests for the same item in
 * a row both fire (a plain "current id" would coalesce them).
 */
import { useEffect, useRef, useSyncExternalStore } from "react";

interface EditRequest {
  readonly itemId: string;
  readonly seq: number;
}

let current: EditRequest | null = null;
let seq = 0;
const listeners = new Set<() => void>();

/** Ask the widget rendering `itemId` to enter its edit / drill-in state. */
export function requestEdit(itemId: string): void {
  current = { itemId, seq: ++seq };
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Run `onRequest` each time an edit is requested for `itemId`. The callback
 * is read through a ref so callers can pass an inline closure without
 * re-subscribing (and without missing a request that lands mid-render).
 */
export function useEditRequest(itemId: string, onRequest: () => void): void {
  const mine = useSyncExternalStore(
    subscribe,
    () => (current?.itemId === itemId ? current.seq : 0),
    () => 0,
  );
  const cbRef = useRef(onRequest);
  cbRef.current = onRequest;
  useEffect(() => {
    if (mine > 0) cbRef.current();
  }, [mine]);
}

/** Test seam: forget any pending request. */
export function resetEditRequests(): void {
  current = null;
}
